import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { SECTORS, STOCK_LIST } from "./stockList";

// ─── Kite API helpers ─────────────────────────────────────────────────────────

const KITE_BASE = "https://api.kite.trade";

function buildHeaders(apiKey, accessToken) {
  return {
    "X-Kite-Version": "3",
    Authorization: `token ${apiKey}:${accessToken}`,
  };
}

function toDateStr(d) {
  return d.toISOString().slice(0, 10);
}

async function fetchCandles(token, interval, fromDate, toDate, headers) {
  const url = `${KITE_BASE}/instruments/historical/${token}/${interval}?from=${fromDate}&to=${toDate}&continuous=0&oi=0`;
  const res = await fetch(url, { headers });
  if (!res.ok) throw new Error(`HTTP ${res.status} for token ${token}`);
  const json = await res.json();
  return json?.data?.candles || [];
}

// ─── Calculation helpers ──────────────────────────────────────────────────────

function round2(n) {
  return Math.round(n * 100) / 100;
}

function calcPct(a, b) {
  if (!b) return 0;
  return round2(((a - b) / b) * 100);
}

function calcPivots(pH, pL, pC) {
  const pivot = round2((pH + pL + pC) / 3);
  const bc = round2((pH + pL) / 2);
  const tc = round2(pivot - bc + pivot);
  const cpr = `${Math.min(bc, tc).toFixed(2)}-${Math.max(bc, tc).toFixed(2)}`;
  const r1 = round2(2 * pivot - pL);
  const r2 = round2(pivot + (pH - pL));
  const s1 = round2(2 * pivot - pH);
  const s2 = round2(pivot - (pH - pL));
  return { pivot, cpr, r1, r2, s1, s2 };
}

function calcGapFill(open, low, high, prevClose) {
  const gap = open - prevClose;
  if (gap > 0 && low <= prevClose) return "DOWN";
  if (gap < 0 && high >= prevClose) return "UP";
  return "";
}

function processStock(stock, dailyCandles, intradayCandles) {
  // dailyCandles: [timestamp, open, high, low, close, volume, oi]
  // Need at least 2 days of data (yesterday + today)
  // For avg volume use last 20 days
  if (!dailyCandles || dailyCandles.length < 2) return null;

  const today = dailyCandles[dailyCandles.length - 1];
  const prev = dailyCandles[dailyCandles.length - 2];

  const [, prevOpen, prevHigh, prevLow, prevClose, prevVol] = prev;
  const [, todayOpen, todayHigh, todayLow, todayClose, todayVol] = today;

  // Use latest intraday candle for live CMP if available
  let cmp = todayClose;
  let high = todayHigh;
  let low = todayLow;
  let vol = todayVol;

  if (intradayCandles && intradayCandles.length > 0) {
    const last = intradayCandles[intradayCandles.length - 1];
    cmp = last[4]; // close
    high = Math.max(...intradayCandles.map((c) => c[2]));
    low = Math.min(...intradayCandles.map((c) => c[3]));
    vol = intradayCandles.reduce((s, c) => s + c[5], 0);
  }

  // Average daily volume (last N daily candles excluding today)
  const pastCandles = dailyCandles.slice(0, -1);
  const avgVol = pastCandles.length
    ? pastCandles.reduce((s, c) => s + c[5], 0) / pastCandles.length
    : prevVol || 1;

  const chgPct = calcPct(cmp, prevClose);
  const intraPct = calcPct(cmp, todayOpen);
  const gapPct = calcPct(todayOpen, prevClose);
  const gapFill = calcGapFill(todayOpen, low, high, prevClose);
  const volPct = round2((vol / avgVol) * 100);
  const preVolPct = prevVol ? round2((vol / prevVol) * 100) : 0;

  const { pivot, cpr, r1, r2, s1, s2 } = calcPivots(
    prevHigh,
    prevLow,
    prevClose,
  );

  return {
    symbol: stock.symbol,
    sector: stock.sector,
    indexify: stock.indexify,
    cmp: round2(cmp),
    open: round2(todayOpen),
    high: round2(high),
    low: round2(low),
    chgPct,
    intraPct,
    gapPct,
    gapFill,
    volPct,
    preVolPct,
    cpr,
    pivot,
    r1,
    r2,
    s1,
    s2,
  };
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function PctCell({ value }) {
  const color = value > 0 ? "#22c55e" : value < 0 ? "#ef4444" : "#9ca3af";
  return (
    <td
      style={{
        color,
        fontWeight: 500,
        textAlign: "right",
        padding: "6px 10px",
        whiteSpace: "nowrap",
      }}
    >
      {value > 0 ? "+" : ""}
      {value?.toFixed(2)}%
    </td>
  );
}

function GapFillCell({ value }) {
  if (!value)
    return <td style={{ padding: "6px 10px", textAlign: "center" }}>–</td>;
  const color = value === "UP" ? "#22c55e" : "#ef4444";
  return (
    <td
      style={{
        color,
        fontWeight: 700,
        textAlign: "center",
        padding: "6px 10px",
      }}
    >
      {value}
    </td>
  );
}

const COL_DEFS = [
  { key: "symbol", label: "SYMBOL", align: "left" },
  { key: "indexify", label: "INDEXIFY", align: "center" },
  { key: "sector", label: "SECTOR", align: "left" },
  { key: "cmp", label: "CMP", align: "right" },
  { key: "open", label: "OPEN", align: "right" },
  { key: "high", label: "HIGH", align: "right" },
  { key: "low", label: "LOW", align: "right" },
  { key: "chgPct", label: "CHG %", align: "right", pct: true },
  { key: "intraPct", label: "INTRA %", align: "right", pct: true },
  { key: "gapPct", label: "GAP %", align: "right", pct: true },
  { key: "gapFill", label: "GAP FILL", align: "center", gapFill: true },
  { key: "volPct", label: "VOL %", align: "right" },
  { key: "preVolPct", label: "PRE VOL%", align: "right" },
  { key: "cpr", label: "CPR", align: "right" },
  { key: "s2", label: "S2", align: "right" },
  { key: "s1", label: "S1", align: "right" },
  { key: "pivot", label: "PIVOT", align: "right" },
  { key: "r1", label: "R1", align: "right" },
  { key: "r2", label: "R2", align: "right" },
];

// ─── Main Component ───────────────────────────────────────────────────────────

export default function FataFatScanner() {
  const [apiKey, setApiKey] = useState(
    localStorage.getItem("kite_api_key") || "",
  );
  const [accessToken, setAccessToken] = useState(
    localStorage.getItem("kite_access_token") || "",
  );
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [error, setError] = useState("");
  const [sortCol, setSortCol] = useState("symbol");
  const [sortAsc, setSortAsc] = useState(true);
  const [filterSector, setFilterSector] = useState("ALL");
  const [filterIdx, setFilterIdx] = useState("ALL");
  const [search, setSearch] = useState("");
  const [lastFetched, setLastFetched] = useState(null);
  const abortRef = useRef(null);

  const today = new Date();
  const fromDate = toDateStr(new Date(today - 30 * 24 * 60 * 60 * 1000)); // 30 days back for avg vol
  const toDate = toDateStr(today);

  const saveCredentials = () => {
    localStorage.setItem("kite_api_key", apiKey);
    localStorage.setItem("kite_access_token", accessToken);
  };

  const fetchAll = useCallback(async () => {
    if (!apiKey || !accessToken) {
      setError("Please enter your Kite API Key and Access Token.");
      return;
    }
    setError("");
    setLoading(true);
    setRows([]);
    setProgress({ done: 0, total: STOCK_LIST.length });
    saveCredentials();

    const controller = new AbortController();
    abortRef.current = controller;
    const headers = buildHeaders(apiKey, accessToken);

    const results = [];
    const BATCH = 5; // parallel requests per batch

    for (let i = 0; i < STOCK_LIST.length; i += BATCH) {
      if (controller.signal.aborted) break;
      const batch = STOCK_LIST.slice(i, i + BATCH);

      const batchResults = await Promise.allSettled(
        batch.map(async (stock) => {
          const [daily, intraday] = await Promise.all([
            fetchCandles(stock.token, "day", fromDate, toDate, headers),
            fetchCandles(stock.token, "5minute", toDate, toDate, headers),
          ]);
          const row = processStock(stock, daily, intraday);
          return row;
        }),
      );

      for (const r of batchResults) {
        if (r.status === "fulfilled" && r.value) results.push(r.value);
      }

      setProgress((p) => ({
        ...p,
        done: Math.min(i + BATCH, STOCK_LIST.length),
      }));
      // small throttle to avoid rate-limit (3 req/sec per Kite docs)
      await new Promise((res) => setTimeout(res, 400));
    }

    setRows(results);
    setLastFetched(new Date().toLocaleTimeString());
    setLoading(false);
  }, [apiKey, accessToken]);

  const stopFetch = () => {
    abortRef.current?.abort();
    setLoading(false);
  };

  // Sort & filter
  const displayRows = useMemo(() => {
    let data = [...rows];
    if (filterSector !== "ALL")
      data = data.filter((r) => r.sector === filterSector);
    if (filterIdx !== "ALL")
      data = data.filter((r) => r.indexify === filterIdx);
    if (search)
      data = data.filter((r) =>
        r.symbol.toLowerCase().includes(search.toLowerCase()),
      );

    data.sort((a, b) => {
      const va = a[sortCol];
      const vb = b[sortCol];
      if (typeof va === "number" && typeof vb === "number")
        return sortAsc ? va - vb : vb - va;
      return sortAsc
        ? String(va).localeCompare(String(vb))
        : String(vb).localeCompare(String(va));
    });
    return data;
  }, [rows, filterSector, filterIdx, search, sortCol, sortAsc]);

  const handleSort = (key) => {
    if (sortCol === key) setSortAsc((a) => !a);
    else {
      setSortCol(key);
      setSortAsc(true);
    }
  };

  // ── Styles ─────────────────────────────────────────────────────────────────

  const s = {
    wrapper: {
      minHeight: "100vh",
      background: "#0f172a",
      color: "#e2e8f0",
      fontFamily: "'Inter', 'Segoe UI', sans-serif",
      padding: "16px",
    },
    header: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      flexWrap: "wrap",
      gap: "12px",
      marginBottom: "16px",
    },
    title: {
      fontSize: "20px",
      fontWeight: 700,
      color: "#38bdf8",
      letterSpacing: "0.5px",
    },
    card: {
      background: "#1e293b",
      borderRadius: "10px",
      padding: "16px",
      marginBottom: "16px",
      border: "1px solid #334155",
    },
    label: {
      fontSize: "11px",
      color: "#94a3b8",
      marginBottom: "4px",
      display: "block",
    },
    input: {
      background: "#0f172a",
      border: "1px solid #334155",
      borderRadius: "6px",
      color: "#e2e8f0",
      padding: "8px 12px",
      fontSize: "13px",
      width: "100%",
      outline: "none",
    },
    btn: (variant = "primary") => ({
      padding: "9px 20px",
      borderRadius: "7px",
      border: "none",
      cursor: "pointer",
      fontWeight: 600,
      fontSize: "13px",
      background:
        variant === "primary"
          ? "#0ea5e9"
          : variant === "danger"
            ? "#ef4444"
            : "#334155",
      color: "#fff",
      transition: "opacity 0.15s",
    }),
    filterRow: {
      display: "flex",
      flexWrap: "wrap",
      gap: "10px",
      alignItems: "center",
      marginBottom: "12px",
    },
    select: {
      background: "#1e293b",
      border: "1px solid #334155",
      borderRadius: "6px",
      color: "#e2e8f0",
      padding: "7px 12px",
      fontSize: "13px",
      cursor: "pointer",
      outline: "none",
    },
    searchInput: {
      background: "#1e293b",
      border: "1px solid #334155",
      borderRadius: "6px",
      color: "#e2e8f0",
      padding: "7px 12px",
      fontSize: "13px",
      outline: "none",
      width: "180px",
    },
    tableWrapper: {
      overflowX: "auto",
      borderRadius: "10px",
      border: "1px solid #334155",
    },
    table: {
      width: "100%",
      borderCollapse: "collapse",
      fontSize: "12px",
    },
    th: (align = "left") => ({
      background: "#1e293b",
      color: "#94a3b8",
      padding: "10px 10px",
      textAlign: align,
      fontWeight: 700,
      fontSize: "11px",
      letterSpacing: "0.5px",
      borderBottom: "1px solid #334155",
      whiteSpace: "nowrap",
      cursor: "pointer",
      userSelect: "none",
      position: "sticky",
      top: 0,
      zIndex: 1,
    }),
    td: (align = "left") => ({
      padding: "6px 10px",
      textAlign: align,
      borderBottom: "1px solid #1e293b",
      whiteSpace: "nowrap",
      color: "#e2e8f0",
    }),
    trEven: { background: "#0f172a" },
    trOdd: { background: "#111827" },
    progress: {
      height: "4px",
      background: "#1e293b",
      borderRadius: "2px",
      overflow: "hidden",
      marginBottom: "12px",
    },
    progressBar: (pct) => ({
      height: "100%",
      width: `${pct}%`,
      background: "linear-gradient(90deg, #0ea5e9, #38bdf8)",
      transition: "width 0.3s",
    }),
    indexBadge: (idx) => ({
      display: "inline-block",
      padding: "2px 8px",
      borderRadius: "99px",
      fontSize: "11px",
      fontWeight: 700,
      background: idx === "A" ? "#064e3b" : idx === "B" ? "#1e3a5f" : "#2d1b69",
      color: idx === "A" ? "#34d399" : idx === "B" ? "#60a5fa" : "#a78bfa",
    }),
  };

  const progressPct = progress.total
    ? Math.round((progress.done / progress.total) * 100)
    : 0;

  return (
    <div style={s.wrapper}>
      {/* ── Header ── */}
      <div style={s.header}>
        <div>
          <div style={s.title}>FataFat Intraday Scanner</div>
          <div style={{ fontSize: "11px", color: "#64748b", marginTop: "2px" }}>
            Powered by Kite Connect API
            {lastFetched && ` · Last fetched: ${lastFetched}`}
          </div>
        </div>
        <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
          {loading ? (
            <button style={s.btn("danger")} onClick={stopFetch}>
              Stop
            </button>
          ) : (
            <button
              style={s.btn("primary")}
              onClick={fetchAll}
              disabled={loading}
            >
              {rows.length ? "Refresh Data" : "Fetch Data"}
            </button>
          )}
        </div>
      </div>

      {/* ── Credentials ── */}
      <div style={s.card}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr auto",
            gap: "12px",
            alignItems: "flex-end",
          }}
        >
          <div>
            <span style={s.label}>Kite API Key</span>
            <input
              style={s.input}
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder="your_api_key"
              spellCheck={false}
            />
          </div>
          <div>
            <span style={s.label}>Access Token (daily)</span>
            <input
              style={s.input}
              type="password"
              value={accessToken}
              onChange={(e) => setAccessToken(e.target.value)}
              placeholder="your_access_token"
              spellCheck={false}
            />
          </div>
          <button style={s.btn("secondary")} onClick={saveCredentials}>
            Save
          </button>
        </div>
        {error && (
          <div
            style={{ color: "#f87171", fontSize: "12px", marginTop: "10px" }}
          >
            {error}
          </div>
        )}
        <div style={{ color: "#64748b", fontSize: "11px", marginTop: "10px" }}>
          Note: Kite access tokens expire daily. You must re-generate via
          KiteConnect login each day. If you see CORS errors, set up a backend
          proxy at <code>/api/kite-historical</code>.
        </div>
      </div>

      {/* ── Progress ── */}
      {loading && (
        <div>
          <div
            style={{ fontSize: "12px", color: "#94a3b8", marginBottom: "6px" }}
          >
            Fetching {progress.done} / {progress.total} stocks…
          </div>
          <div style={s.progress}>
            <div style={s.progressBar(progressPct)} />
          </div>
        </div>
      )}

      {/* ── Filters ── */}
      {rows.length > 0 && (
        <div style={s.filterRow}>
          <input
            style={s.searchInput}
            placeholder="Search symbol…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <select
            style={s.select}
            value={filterSector}
            onChange={(e) => setFilterSector(e.target.value)}
          >
            {SECTORS.map((sec) => (
              <option key={sec} value={sec}>
                {sec}
              </option>
            ))}
          </select>
          <select
            style={s.select}
            value={filterIdx}
            onChange={(e) => setFilterIdx(e.target.value)}
          >
            <option value="ALL">All Grades</option>
            <option value="A">A (Nifty50)</option>
            <option value="B">B (Nifty100/200)</option>
            <option value="C">C (Nifty500+)</option>
          </select>
          <div
            style={{ color: "#64748b", fontSize: "12px", marginLeft: "auto" }}
          >
            {displayRows.length} of {rows.length} stocks
          </div>
        </div>
      )}

      {/* ── Table ── */}
      {displayRows.length > 0 && (
        <div style={s.tableWrapper}>
          <table style={s.table}>
            <thead>
              <tr>
                {COL_DEFS.map((col) => (
                  <th
                    key={col.key}
                    style={s.th(col.align)}
                    onClick={() => handleSort(col.key)}
                  >
                    {col.label}
                    {sortCol === col.key ? (sortAsc ? " ▲" : " ▼") : ""}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {displayRows.map((row, i) => (
                <tr key={row.symbol} style={i % 2 === 0 ? s.trEven : s.trOdd}>
                  {/* Symbol */}
                  <td
                    style={{
                      ...s.td("left"),
                      fontWeight: 600,
                      color: "#38bdf8",
                    }}
                  >
                    <a
                      href={`https://kite.zerodha.com/chart/ext/ciq/NSE/${row.symbol}/1D`}
                      target="_blank"
                      rel="noreferrer"
                      style={{ color: "#38bdf8", textDecoration: "none" }}
                    >
                      {row.symbol}
                    </a>
                  </td>

                  {/* Indexify */}
                  <td style={s.td("center")}>
                    <span style={s.indexBadge(row.indexify)}>
                      {row.indexify}
                    </span>
                  </td>

                  {/* Sector */}
                  <td
                    style={{
                      ...s.td("left"),
                      color: "#94a3b8",
                      fontSize: "11px",
                    }}
                  >
                    {row.sector}
                  </td>

                  {/* CMP */}
                  <td style={{ ...s.td("right"), fontWeight: 600 }}>
                    {row.cmp}
                  </td>

                  {/* Open */}
                  <td style={s.td("right")}>{row.open}</td>

                  {/* High */}
                  <td style={{ ...s.td("right"), color: "#22c55e" }}>
                    {row.high}
                  </td>

                  {/* Low */}
                  <td style={{ ...s.td("right"), color: "#ef4444" }}>
                    {row.low}
                  </td>

                  {/* CHG% */}
                  <PctCell value={row.chgPct} />

                  {/* INTRA% */}
                  <PctCell value={row.intraPct} />

                  {/* GAP% */}
                  <PctCell value={row.gapPct} />

                  {/* GAP FILL */}
                  <GapFillCell value={row.gapFill} />

                  {/* VOL% */}
                  <td
                    style={{
                      ...s.td("right"),
                      color: row.volPct > 100 ? "#fbbf24" : "#e2e8f0",
                    }}
                  >
                    {row.volPct}%
                  </td>

                  {/* PRE VOL% */}
                  <td style={s.td("right")}>{row.preVolPct}%</td>

                  {/* CPR */}
                  <td
                    style={{
                      ...s.td("right"),
                      color: "#a78bfa",
                      fontSize: "11px",
                    }}
                  >
                    {row.cpr}
                  </td>

                  {/* S2, S1 */}
                  <td style={{ ...s.td("right"), color: "#f87171" }}>
                    {row.s2}
                  </td>
                  <td style={{ ...s.td("right"), color: "#fb923c" }}>
                    {row.s1}
                  </td>

                  {/* PIVOT */}
                  <td
                    style={{
                      ...s.td("right"),
                      fontWeight: 600,
                      color: "#fbbf24",
                    }}
                  >
                    {row.pivot}
                  </td>

                  {/* R1, R2 */}
                  <td style={{ ...s.td("right"), color: "#4ade80" }}>
                    {row.r1}
                  </td>
                  <td style={{ ...s.td("right"), color: "#22c55e" }}>
                    {row.r2}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Empty state ── */}
      {!loading && rows.length === 0 && (
        <div
          style={{
            textAlign: "center",
            padding: "60px 20px",
            color: "#475569",
          }}
        >
          <div style={{ fontSize: "48px", marginBottom: "12px" }}>📊</div>
          <div style={{ fontSize: "16px", fontWeight: 600, color: "#64748b" }}>
            Enter your Kite credentials and click "Fetch Data"
          </div>
          <div style={{ fontSize: "13px", marginTop: "8px", color: "#475569" }}>
            The scanner will calculate CMP, CHG%, INTRA%, GAP%, VOL%, Pivot
            levels and more for all NSE F&O stocks using Kite Connect historical
            candle API.
          </div>
        </div>
      )}
    </div>
  );
}
